/**
 * A rule segment's rules on its own page: read-only sentences, an Edit button,
 * and — when editing — the builder in place.
 *
 * The rules are read through the catalogue so a renamed column reads with its
 * new name. If the catalogue cannot be read, the rules are still shown, by key:
 * what the segment holds does not depend on its labels, and hiding the rules
 * behind a failed label lookup would leave a person unable to see what their
 * segment means. A retry is offered beside them.
 *
 * Editing swaps the summary for `RuleSegmentBuilder` in edit mode, which saves
 * with `PATCH /segments/:id { rules }`. The API refreshes members in the same
 * request, so the segment it answers with — counts, `rulesRefreshedAt` — is
 * handed straight back to the page, and the timeline re-reads on the new
 * `updatedAt`.
 */
import type { Segment } from '@wend/shared';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@wend/ui';
import { Pencil, RotateCw } from 'lucide-react';
import { useState } from 'react';

import { RuleTreeSummary } from '../../components/rules';
import { formatDateTime } from '../../lib/format';
import { useFieldCatalogue } from '../../lib/use-field-catalogue';
import { DYNAMIC_SENTENCE } from './RuleBuilderWorkspace';
import { RuleSegmentBuilder } from './RuleSegmentBuilder';

export function RuleSegmentRules({ segment, onSaved }: { segment: Segment; onSaved: (segment: Segment) => void }) {
  const [editing, setEditing] = useState(false);
  const { catalogue, error, reload } = useFieldCatalogue();

  if (editing) {
    return (
      <RuleSegmentBuilder
        mode="edit"
        segment={segment}
        onSaved={(updated) => {
          onSaved(updated);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rules</CardTitle>
        <CardDescription>{DYNAMIC_SENTENCE}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {segment.rules === null ? (
          <p className="text-[0.88rem] text-muted">This segment has no rules on record.</p>
        ) : (
          <RuleTreeSummary tree={segment.rules} fields={catalogue?.fields ?? []} />
        )}
        {catalogue === null && error !== null && (
          <p className="flex flex-wrap items-center gap-2 text-[0.8rem] text-warn" role="status">
            The field names could not be loaded, so the rules show their keys.
            <Button type="button" variant="secondary" size="sm" onClick={reload}>
              <RotateCw aria-hidden="true" className="size-4" />
              Try again
            </Button>
          </p>
        )}
        <p className="font-mono text-[0.68rem] text-muted">
          {segment.rulesRefreshedAt === null
            ? 'Members not brought up to date yet'
            : `Members brought up to date ${formatDateTime(segment.rulesRefreshedAt)}`}
        </p>
        <div>
          <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(true)}>
            <Pencil aria-hidden="true" className="size-4" />
            Edit rules
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
